import React, { useCallback, useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { notBlank } from "../../util/ext";
import { equals, none } from "ramda";
import clsx from "clsx";
import { useDebounce } from "../../util/hooks";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";

import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import OutlinedInput from "@mui/material/OutlinedInput";
import SearchIcon from "@mui/icons-material/Search";

import FontAwesomeIcon from "../../components/FontAwesomeIcon";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
  },

  toolbar: {
    display: "flex",
    alignItems: "center",
    flexGrow: 1,
    borderWidth: 2,
    borderStyle: "solid",
    borderColor: "white",
    background: "white",
    padding: theme.spacing(4),
  },

  dense: {
    padding: theme.spacing(0),
  },

  leftContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    width: "33%",
  },

  title: {
    flexGrow: 1,
    display: "none",
    [theme.breakpoints.up("sm")]: {
      display: "flex",
      alignItems: "center",
    },
  },

  count: {
    marginRight: 10,
  },

  addButton: {
    margin: 20,
  },

  search: {
    backgroundColor: theme.palette.primary.light,
    borderRadius: "4px",
    position: "relative",
    width: "100%",
    display: "flex",
    alignItems: "center",
  },

  searchIcon: {
    color: theme.palette.primary.darker,
  },

  inputRoot: {
    color: theme.palette.primary.darker,
    flexGrow: 1,
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: `2px solid ${theme.palette.primary.brandPrimary}`,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: `2px solid ${theme.palette.primary.brandPrimary}`,
    },
  },

  inputFocused: {
    borderColor: theme.palette.primary.brandPrimary,
  },

  outline: {
    borderColor: "transparent",
  },

  inputInput: {
    width: "100%",
    padding: theme.spacing(2.5),
  },
}));

const RealTimeSearchBar = ({
  backButton,
  query,
  onQueryChange,
  otherFilters,
  filterMenu,
  debounceDelay,
  dense = false,
  searchBarEnd = false,
  placeHolder = "Search...",
}) => {
  const classes = useStyles();
  const [searchTerm, setSearchTerm] = useState(query);
  const debouncedSearchTerm = useDebounce(searchTerm, debounceDelay || 500);

  const handleSearchTermChange = useCallback(
    event => {
      setSearchTerm(event.target.value);
    },
    [setSearchTerm],
  );

  useEffect(() => {
    // Search endpoint handles queries as Arrays
    if (Array.isArray(query)) {
      if (none(equals(debouncedSearchTerm), query)) {
        onQueryChange([debouncedSearchTerm]);
      }
    } else {
      if (query !== debouncedSearchTerm) {
        onQueryChange(debouncedSearchTerm);
      }
    }
  }, [query, debouncedSearchTerm, onQueryChange]);

  const handleSearchTermClear = () => {
    setSearchTerm("");
  };

  return (
    <div className={classes.root}>
      <Box
        className={clsx(classes.toolbar, { [classes.dense]: dense })}
        justifyContent={searchBarEnd ? "end" : "justify-start"}
      >
        {notBlank(backButton) && <Box mr={2}>{backButton}</Box>}
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="flex-start"
          flexBasis={searchBarEnd ? "100%" : "33%"}
        >
          <div className={classes.search}>
            <OutlinedInput
              autoFocus
              type="search"
              placeholder={placeHolder}
              classes={{
                root: classes.inputRoot,
                input: classes.inputInput,
                notchedOutline: classes.outline,
                focused: classes.inputFocused,
              }}
              onChange={handleSearchTermChange}
              inputProps={{ "aria-label": "search" }}
              value={searchTerm || ""}
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon
                    fontSize="large"
                    classes={{ root: classes.searchIcon }}
                  />
                </InputAdornment>
              }
              endAdornment={
                <InputAdornment position="end">
                  <IconButton onClick={handleSearchTermClear} size="large">
                    <FontAwesomeIcon icon={faXmark} variant="darker" />
                  </IconButton>
                </InputAdornment>
              }
            />
          </div>
        </Box>
        {notBlank(filterMenu) && (
          <Box ml={2} flexBasis="33%">
            {filterMenu}
          </Box>
        )}
        {notBlank(otherFilters) && (
          <Box ml={2} flexBasis={notBlank(filterMenu) ? "33%" : "66%"}>
            {otherFilters}
          </Box>
        )}
      </Box>
    </div>
  );
};

export default RealTimeSearchBar;
